import logging from "web/script/utils/logging";
import globals from "web/script/modules/globals";
import style from "web/script/dom/style";


const runners = new Set();
let running = false;

/**
 * Add a new function to be executed on each loop iteration.
 * @param {Function} fn
 * @returns {Function} A function that will call `remove` with `fn`.
 */
export function add(fn) {
    runners.add(fn);
    start();
    return () => remove(fn);
}

/**
 * Removes `fn` from the list of functions to be executed on each loop iteration.
 * @param {Function} fn The function to remove
 * @returns {Boolean} true if the function was removed else false
 */
export function remove(fn) {
    const removed = runners.delete(fn);
    if (!runners.size) {
        stop();
    }
    return removed;
}

/**
 * Clears the list of functions to be run on each loop iteration. Very destructive but useful for tests.
 */
export function reset() {
    runners.clear();
    stop();
}

/**
 * Adds a scroll watcher function. This function will be called whenever the scroll position changes.
 * @param {Function} fn The function to be called when the scroll position changes. Will be passed `window.pageYOffset`.
 * @returns {Function} A function than can be used to remove the watcher.
 */
export function addScrollWatcher(fn) {
    let lastScrollPosition = null;
    return add(() => {
        const { pageYOffset } = globals.window;
        if (pageYOffset !== lastScrollPosition) {
            lastScrollPosition = pageYOffset;
            return fn(pageYOffset);
        }
    });
}

/**
 * Adds an element visibility watcher function. This function will be called whenever the visiblity of an element changes.
 * @param {HTMLElement }element
 * @param {Function} fn The function to call when the elements visibility changes. It will be passed a boolean indicating the visibility state.
 * @param {Number = 250} offset How far off-screen an element can be while still being considered visible.
 * @returns {Function} A function than can be used to remove the watcher.
 */
export function addVisibilityWatcher(element, fn, offset = 250) {
    let lastIsVisible = false;

    return add(() => {
        const boundingRect = style.getPosition(element);
        let isVisible = false;

        if (
            boundingRect.top === 0 &&
            boundingRect.bottom === 0 &&
            boundingRect.left === 0 &&
            boundingRect.right === 0
        ) {
            // if all these values are 0 then the element is almost definitely not visible on the page
            isVisible = false;
        } else if (boundingRect.top <= 0) {
            isVisible = boundingRect.top > -offset;
        } else {
            isVisible = boundingRect.top - globals.window.innerHeight <= offset;
        }

        if (isVisible != lastIsVisible) {
            return fn(isVisible);
        }

        lastIsVisible = isVisible;
    });
}

function start() {
    if (running) {
        return;
    }

    running = true;
    globals.window.requestAnimationFrame(worker);
}

function stop() {
    running = false;
}

function worker() {
    if (!running) {
        return;
    }

    const toRemove = [];

    for (const runner of runners) {
        let result = null;

        try {
            result = runner();
        } catch (e) {
            logging.error(e);
        }

        if (result) {
            toRemove.push(runner);
        }
    }

    for (const runner of toRemove) {
        remove(runner);
    }

    if (running) {
        globals.window.requestAnimationFrame(worker);
    }
}

export default {
    add,
    addScrollWatcher,
    addVisibilityWatcher,
    remove,
};
